import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import type { Editor } from '@tiptap/react';
import { MediaPicker } from '../media/MediaPicker';
import { Button } from '../ui';
import { CONTENT_EXTENSIONS, setEditorContext } from './extensions';
import type { EditorSponsor } from './extensions';

/**
 * The event page editor (D-88, PARTS 1, 2).
 *
 * The toolbar is the whole permission model for what a page may look like:
 * there is no font picker, no color picker, no size control and no HTML view,
 * because none of those exist as nodes. Headings start at level two — the
 * page's one h1 is the event's title — and a link is checked before it is
 * accepted. What an author cannot express here cannot reach the storefront.
 *
 * Work is autosaved as a draft every few seconds and published deliberately,
 * so a closed laptop costs nothing and a half-written sentence never appears
 * on a live page.
 */

const AUTOSAVE_MS = 4000;

export function ContentEditor({
  content,
  sponsors,
  busy,
  onAutosave,
  onPublish,
  onPreview,
  onDirtyChange,
}: {
  content: unknown;
  sponsors: EditorSponsor[];
  busy: boolean;
  onAutosave: (doc: unknown) => void;
  onPublish: (doc: unknown, note: string | null) => void;
  onPreview: (doc: unknown) => void;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const [picking, setPicking] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [note, setNote] = useState('');
  const autosaveTimer = useRef<number | null>(null);

  // The node views read the sponsors from here rather than from props, because
  // ProseMirror owns their lifetime, not React's tree.
  useMemo(() => setEditorContext({ sponsors }), [sponsors]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // No level-one heading: the page already has its one h1.
        heading: { levels: [2, 3, 4] },
        // These are in the document model, so they stay; everything else the
        // kit offers that we do not render (code blocks, images by URL) is off.
        codeBlock: false,
        link: false,
      }),
      Link.configure({
        openOnClick: false,
        autolink: false,
        // The server checks again, but an author should be told at once.
        protocols: ['https', 'mailto', 'tel'],
        HTMLAttributes: { rel: 'noopener nofollow' },
      }),
      Placeholder.configure({ placeholder: 'What is this event? Write it the way you would say it out loud.' }),
      ...CONTENT_EXTENSIONS,
    ],
    content: (content as never) ?? undefined,
    editorProps: {
      attributes: { class: 'content-editor prose-admin min-h-64 rounded border border-line bg-surface px-4 py-3 focus:outline-none' },
    },
    onUpdate: () => {
      setDirty(true);
      onDirtyChange?.(true);
    },
  });

  // Autosave: quiet, on a timer, never while the document is untouched.
  useEffect(() => {
    if (!editor || !dirty) return;
    if (autosaveTimer.current) window.clearTimeout(autosaveTimer.current);
    autosaveTimer.current = window.setTimeout(() => onAutosave(editor.getJSON()), AUTOSAVE_MS);
    return () => {
      if (autosaveTimer.current) window.clearTimeout(autosaveTimer.current);
    };
  }, [editor, dirty, onAutosave]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const previous = editor.getAttributes('link')['href'] as string | undefined;
    const href = window.prompt('Link to (a path like /events/haunted, or https://…)', previous ?? '');
    if (href === null) return;
    if (href === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    const allowed = href.startsWith('/') || href.startsWith('#') || /^(https|mailto|tel):/i.test(href);
    if (!allowed) {
      window.alert('Links must be a path on this site, or start with https:, mailto: or tel:.');
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
  }, [editor]);

  if (!editor) return <p className="text-sm text-ink-400">Loading the editor…</p>;

  return (
    <div className="space-y-3">
      <Toolbar editor={editor} onPickImage={() => setPicking(true)} onLink={setLink} sponsors={sponsors} />

      <EditorContent editor={editor} />

      <div className="flex flex-wrap items-center gap-2">
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="What changed (optional)" aria-label="What changed" className="w-64 rounded border border-line bg-surface px-2 py-1.5 text-sm" />
        <Button onClick={() => onPreview(editor.getJSON())}>Preview</Button>
        <Button
          variant="primary"
          disabled={busy}
          onClick={() => {
            onPublish(editor.getJSON(), note.trim() || null);
            setNote('');
            setDirty(false);
            onDirtyChange?.(false);
          }}
        >
          {busy ? 'Publishing…' : 'Publish'}
        </Button>
        <span className="text-xs text-ink-400">{dirty ? 'Unsaved changes — autosaving' : 'Saved'}</span>
      </div>

      <MediaPicker
        open={picking}
        onClose={() => setPicking(false)}
        category="CONTENT"
        title="Choose an image for this page"
        onChoose={(asset) => editor.chain().focus().insertLibraryImage({ fileId: asset.id, alt: asset.alt_text ?? '' }).run()}
      />
    </div>
  );
}

function Toolbar({ editor, onPickImage, onLink, sponsors }: { editor: Editor; onPickImage: () => void; onLink: () => void; sponsors: EditorSponsor[] }) {
  const item = (label: string, active: boolean, action: () => void, title?: string) => (
    <button
      key={label}
      type="button"
      onClick={action}
      aria-pressed={active}
      title={title ?? label}
      className={`rounded px-2 py-1 text-sm ${active ? 'bg-accent text-white' : 'text-ink-900 hover:bg-page'}`}
    >
      {label}
    </button>
  );

  return (
    <div className="flex flex-wrap items-center gap-1 rounded border border-line bg-page px-2 py-1.5">
      {item('B', editor.isActive('bold'), () => editor.chain().focus().toggleBold().run(), 'Bold')}
      {item('I', editor.isActive('italic'), () => editor.chain().focus().toggleItalic().run(), 'Italic')}
      {item('U', editor.isActive('underline'), () => editor.chain().focus().toggleUnderline().run(), 'Underline')}
      {item('S', editor.isActive('strike'), () => editor.chain().focus().toggleStrike().run(), 'Strikethrough')}
      <span className="mx-1 h-5 w-px bg-line" />
      {item('H2', editor.isActive('heading', { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), 'Section heading')}
      {item('H3', editor.isActive('heading', { level: 3 }), () => editor.chain().focus().toggleHeading({ level: 3 }).run(), 'Sub-heading')}
      {item('H4', editor.isActive('heading', { level: 4 }), () => editor.chain().focus().toggleHeading({ level: 4 }).run(), 'Small heading')}
      <span className="mx-1 h-5 w-px bg-line" />
      {item('List', editor.isActive('bulletList'), () => editor.chain().focus().toggleBulletList().run(), 'Bulleted list')}
      {item('1.', editor.isActive('orderedList'), () => editor.chain().focus().toggleOrderedList().run(), 'Numbered list')}
      {item('Quote', editor.isActive('blockquote'), () => editor.chain().focus().toggleBlockquote().run(), 'Quote')}
      {item('—', false, () => editor.chain().focus().setHorizontalRule().run(), 'Divider')}
      <span className="mx-1 h-5 w-px bg-line" />
      {item('Link', editor.isActive('link'), onLink, 'Link')}
      {item('Image', false, onPickImage, 'An image from the media library')}
      {item('Button', false, () => editor.chain().focus().insertCta({ label: 'Buy tickets', href: '' }).run(), 'A call to action')}
      <span className="mx-1 h-5 w-px bg-line" />
      {item('Note', editor.isActive('callout', { tone: 'info' }), () => editor.chain().focus().toggleCallout('info').run(), 'A note')}
      {item('Warning', editor.isActive('callout', { tone: 'warning' }), () => editor.chain().focus().toggleCallout('warning').run(), 'A warning')}
      <span className="mx-1 h-5 w-px bg-line" />
      <button
        type="button"
        disabled={sponsors.length === 0}
        title={sponsors.length === 0 ? 'Attach a sponsor to this event first' : 'One sponsor'}
        onClick={() => editor.chain().focus().insertSponsorBlock({ sponsorId: sponsors[0]!.id, label: 'Presented by' }).run()}
        className="rounded px-2 py-1 text-sm text-ink-900 hover:bg-page disabled:opacity-40"
      >
        Sponsor
      </button>
      <button
        type="button"
        disabled={sponsors.length === 0}
        title={sponsors.length === 0 ? 'Attach a sponsor to this event first' : 'Every sponsor at a level'}
        onClick={() => editor.chain().focus().insertSponsorGroup({ heading: 'With support from' }).run()}
        className="rounded px-2 py-1 text-sm text-ink-900 hover:bg-page disabled:opacity-40"
      >
        Sponsor row
      </button>
      <span className="mx-1 h-5 w-px bg-line" />
      {item('Undo', false, () => editor.chain().focus().undo().run(), 'Undo')}
      {item('Redo', false, () => editor.chain().focus().redo().run(), 'Redo')}
    </div>
  );
}
